import React, { useState, useMemo, useEffect } from 'react';
import Pagination from '@components/Layout/Pagination';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'react-bootstrap';
import { FaFilter } from 'react-icons/fa';
import NurseManagementTable from '@components/Layout/NurseManagementTable';
import {
    CREATE_NOTIFICATION_PATH,
    CREATE_DEVICE_PATH,
    CREATE_NURSE_PATH,
    NURSE_PATH,
} from '@constants/routes';
import { useNavigate } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { IoClose } from 'react-icons/io5';
import DownIcon from '@components/SvgComponents/DownIcon';
import UpIcon from '@components/SvgComponents/UpIcon';
import UserTable from '@components/Layout/UserTable';
import {
    fetchCountries,
    getUserInfo,
    userList,
    getNurseList,
} from '@utils/utils';
import { NURSE } from '@constants/constants';
import {
    setAdminCreateIndividual,
    setShowUpdatePassword,
    setUserRole,
    setDeviceUniqueID,
    setHospitalName,
    setHospitalBranch,
    setHospitalID,
    setAddressOne,
    setAddressTwo,
    setCity,
    setUserState,
    setCountry,
    setPincode,
    setHospitalWebsite,
    setHospitalEmail,
    setHospitalContact,
    setHospitalGST,
    setFileMetadata,
    setName,
    setEmail,
    setMobileNumber,
    setPassword,
    setStateList,
    setCountryList,
    setAdminCreateNurse,
    setHospitalAddNurse,
    setAdminCreateHospital,
} from '@store/reducers/auth/signupSlice';
import { useDispatch } from 'react-redux';
import { HOSPITALS_TABLE, USER_INFO_TABLE } from '@constants/dynamoDBTableName';
import { useAppSelector } from '@store/hooks';
import DataLoader from '@components/DataLoader';
import SearchBar from '@components/Layout/SearchBar';

const parseDate = (dateString: string): Date => {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
};

type CustomDropdownToggleProps = {
    children?: React.ReactNode;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

interface NurseData {
    id: string;
    email?: string;
    name?: string;
    deviceId?: string;
    mobileNo?: string;
    createdAt?: string;
    createdAt_time?: string;
    hospitalName?: string;
    hospitalContact?: string;
    status?: string;
    nurseId?: string;
}

const CustomDropdownToggle = React.forwardRef<
    HTMLButtonElement,
    CustomDropdownToggleProps
>(({ children, onClick }, ref) => (
    <button
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            onClick?.(e);
        }}
        className="btn !bg-primary py-2 text-white"
    >
        {children}
        <FaFilter className="ml-2" /> {/* Add your custom icon here */}
    </button>
));
const NurseManagementList = () => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('newest');
    const [sort, setSort] = useState('newest');
    const [filter, setFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const navigate = useNavigate();
    const [filterField, setFilterField] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [nurseData, setNurseData] = useState<NurseData[]>([]);
    const [loading, setLoading] = useState(true);
    const [isSortExpanded, setIsSortExpanded] = useState(true);
    const [isApprovalExpanded, setIsApprovalExpanded] = useState(true);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const loggedInUserTenantID = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );

    const dispatch = useDispatch();

    const fetchData = async () => {
        try {
            const info = await getUserInfo(
                HOSPITALS_TABLE,
                loggedInUserTenantID
            );
            if (info && info.length != 0) {
                const data = info[0];
                const hospital_id = data.hospital_id?.S
                    ? data.hospital_id?.S
                    : '';

                const list = await getNurseList(
                    USER_INFO_TABLE,
                    hospital_id,
                    loggedInUserTenantID
                );
                const filteredList = list.filter(
                    (item) => item !== null
                ) as NurseData[]; // Filter out null values
                setNurseData(filteredList);
            }
        } catch (error: any) {
            console.log('err', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        handleFetchCountries();
        const handleClickOutside = (event: any) => {
            if (
                !event.target.closest('.dropdown-menu') &&
                !event.target.closest('.dropdown-toggle')
            ) {
                setIsDropdownVisible(false);
            }
        };

        document.addEventListener('click', handleClickOutside);

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    const handleFetchCountries = async () => {
        const list = await fetchCountries();
        if (list.countries) {
            dispatch(setCountryList(list.countries));
        } else if (list.error) {
            // Handle error message
            console.error('Error fetching countries:', list.error);
        }
    };

    const [tableHeading, setTableHeading] = useState([
        {
            id: 1,
            title: t('nurse_list_table.nurse_name'),
        },
        {
            id: 2,
            title: t('nurse_list_table.nurse_id'),
        },
        {
            id: 3,
            title: t('nurse_list_table.status'),
        },
        {
            id: 4,
            title: t('nurse_list_table.action'),
        },
    ]);
    const handelCreateNotification = () => {
        navigate(CREATE_NOTIFICATION_PATH);
    };
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleSortChange = (event: string) => {
        setSort(event);
    };
    const handleFilterChange = (value: string) => {
        setFilter(value);
        // setStatusFilter(value);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };
    const handleAddNurse = () => {
        navigate(CREATE_DEVICE_PATH);
    };
    const handleCloseModal = (event: any) => {
        event.stopPropagation();
        setIsDropdownVisible(false);
    };
    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setRowsPerPage(Number(event.target.value));
        setCurrentPage(1); // Reset to first page
    };

    const toggleSortExpand = () => {
        setIsSortExpanded(!isSortExpanded);
    };
    const toggleApprovalExpand = () => {
        setIsApprovalExpanded(!isApprovalExpanded);
    };

    const backArrow = () => {
        // const pathname = location.pathname;
        // if (pathname.startsWith(NURSE_PATH + '/')) {
        // navigate(NURSE_PATH);
        navigate(-1);
        // }
    };
    const filteredUsers = useMemo(() => {
        let filtered = nurseData;

        if (searchTerm) {
            filtered = filtered.filter(
                (nurse) =>
                    nurse.name
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    nurse.nurseId
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
            );
        }

        if (filterField) {
            filtered = filtered.filter((user) =>
                user.status?.toLowerCase().includes(filterField.toLowerCase())
            );
        }

        return filtered;
    }, [searchTerm, filterField, statusFilter, nurseData]);

    const sortedData = useMemo(() => {
        let sorted = [...filteredUsers];

        if (sortField.toLowerCase() == 'alphabetical_order') {
            sorted.sort((a, b) => {
                const nameA = a.name ? a.name.toLowerCase() : '';
                const nameB = b.name ? b.name.toLowerCase() : '';

                return nameA.localeCompare(nameB);
            });
        } else {
            sorted.sort((a: any, b: any) => {
                const dateA = new Date(a.createdAt_time);
                const dateB = new Date(b.createdAt_time);

                if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                    if (sortField.toLowerCase() == 'newest') {
                        return dateB.getTime() - dateA.getTime();
                    } else if (sortField.toLowerCase() == 'oldest') {
                        return dateA.getTime() - dateB.getTime();
                    }
                }

                // Handle cases where one or both dates are invalid
                if (isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                    return sortField.toLowerCase() == 'newest' ? 1 : -1; // Treat invalid dateA as older/newer
                }
                if (!isNaN(dateA.getTime()) && isNaN(dateB.getTime())) {
                    return sortField.toLowerCase() == 'newest' ? -1 : 1; // Treat invalid dateB as older/newer
                }

                return 0;
            });
        }

        return sorted;
    }, [filteredUsers, sortField]);

    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, currentPage, rowsPerPage]);

    const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
    const hasPaginatedUsers = paginatedUsers.length > 0;

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = 3;

        if (totalPages <= 6) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(renderPageButton(i));
            }
        } else {
            if (currentPage <= maxVisiblePages) {
                // Case: currentPage is within the first 'maxVisiblePages'
                for (let i = 1; i <= maxVisiblePages + 1; i++) {
                    pageNumbers.push(renderPageButton(i));
                }
                pageNumbers.push(renderEllipsis());
                pageNumbers.push(renderPageButton(totalPages));
            } else if (currentPage >= totalPages - maxVisiblePages + 1) {
                // Case: currentPage is near the end
                pageNumbers.push(renderPageButton(1));
                pageNumbers.push(renderEllipsis());
                for (
                    let i = totalPages - maxVisiblePages + 1;
                    i <= totalPages;
                    i++
                ) {
                    pageNumbers.push(renderPageButton(i));
                }
            } else {
                // Case: currentPage is somewhere in the middle
                pageNumbers.push(renderPageButton(1));
                pageNumbers.push(renderEllipsis());

                const startPage = currentPage - 1;
                const endPage = currentPage + 1;

                for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(renderPageButton(i));
                }

                pageNumbers.push(renderEllipsis());
                pageNumbers.push(renderPageButton(totalPages));
            }
        }

        return pageNumbers;
    };
    const renderPageButton = (pageNumber: any) => {
        return (
            <button
                key={pageNumber}
                className={`mx-1 pagination-padding py-2 border rounded ${pageNumber === currentPage ? '!bg-primary text-white' : 'bg-white text-black'}`}
                onClick={() => handlePageChange(pageNumber)}
            >
                {pageNumber}
            </button>
        );
    };

    const renderEllipsis = () => {
        return (
            <span
                key="ellipsis"
                className="bg-primary pagination-padding py-2 border rounded"
            >
                ...
            </span>
        );
    };

    async function handelAddUser() {
        // await getUserAttributes();
        dispatch(setUserRole(NURSE));
        dispatch(setAdminCreateNurse(true));
        dispatch(setHospitalAddNurse(true));
        dispatch(setDeviceUniqueID(''));
        dispatch(setHospitalName(''));
        dispatch(setHospitalBranch(''));
        dispatch(setHospitalID(''));
        dispatch(setAddressOne(''));
        dispatch(setAddressTwo(''));
        dispatch(setCity(''));
        dispatch(setUserState(''));
        dispatch(setCountry(''));
        dispatch(setPincode(''));
        dispatch(setHospitalWebsite(''));
        dispatch(setHospitalEmail(''));
        dispatch(setHospitalContact(''));
        dispatch(setHospitalGST(''));
        dispatch(
            setFileMetadata({
                name: '',
                url: '',
                type: '',
            })
        );
        dispatch(setName(''));
        dispatch(setEmail(''));
        dispatch(setMobileNumber(''));
        dispatch(setPassword(''));
        dispatch(setShowUpdatePassword(true));
        navigate(CREATE_NURSE_PATH);
    }

    const handleClear = (event: any) => {
        event.stopPropagation();
        setSortField('newest');
        setSort('newest');
        setFilterField('');
        setFilter('');
        handleCloseModal(event);
    };

    const handleSave = (event: any) => {
        event.stopPropagation();
        setSortField(sort);
        setFilterField(filter);
        handleCloseModal(event);
    };

    const handleDropdownToggle = (event: any) => {
        event.stopPropagation();
        // Prevent the default behavior to keep the dropdown open
        if (event && event.target) {
            // Check if the event target has the data-click-type attribute
            const isButtonClick = event.target.getAttribute('data-click-type');
            // console.log(isButtonClick);

            if (
                isButtonClick === 'btn-clear' ||
                isButtonClick === 'btn-save' ||
                isButtonClick === 'btn-close'
            ) {
                setIsDropdownVisible(false);
            } else {
                // setIsDropdownVisible(true);
                setIsDropdownVisible(!isDropdownVisible);
            }
        }
    };

    return (
        <>
            <div className="mt-4 2xl:max-w-full">
                <div className="flex align-items-center justify-content-between">
                    {/* <div className="flex items-center"> */}
                        <div
                            className="flex items-center cursor-pointer"
                            // onClick={backArrow}
                        >
                            {/* <IoMdArrowRoundBack className="w-8 h-8" />{' '} */}
                            <h1 className="text-lg font-semibold  ml-2">
                                {t('management.nurse.title')}
                            </h1>
                        </div>
                        <button
                            className="bg-primary rounded py-2 btn px-3 text-white ml-4"
                            onClick={handelAddUser}
                        >
                            {t('management.nurse.nurse_btn')}
                        </button>
                    {/* </div> */}
                </div>
                <div className="flex align-items-center justify-content-between">
                    <div className="my-4">
                        <SearchBar
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                        />
                    </div>
                    <div className="">
                        <Dropdown
                            show={isDropdownVisible}
                            onClick={(event) => handleDropdownToggle(event)}
                            data-click-type="btn-open"
                        >
                            <Dropdown.Toggle
                                as={CustomDropdownToggle}
                                variant="primary"
                                id="dropdown-basic"
                            >
                                {t('user_management.hospital_user.filter')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu onClick={(e) => e.stopPropagation()}>
                                <span className="text-sm ml-4 font-bold text-black">
                                    {t('user_management.hospital_user.filter')}
                                </span>
                                <button
                                    className="absolute right-0 top-0.5 cursor-pointer"
                                    onClick={(event) => handleCloseModal(event)}
                                    data-click-type="btn-close"
                                >
                                    <span className="absolute text-xs top-1.5 right-0 mr-3 cursor-pointer flex items-center">
                                        {t('management.device.close')}
                                        <IoClose className="ml-1" />
                                    </span>
                                </button>
                                <Dropdown.Header
                                    className="font-bold text-black  text-sm d-flex justify-content-between align-items-center"
                                    onClick={toggleSortExpand}
                                >
                                    {t('management.device.sort_bt')}
                                    {isSortExpanded ? <DownIcon /> : <UpIcon />}
                                </Dropdown.Header>
                                {isSortExpanded && (
                                    <>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleSortChange('newest')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="newest"
                                                value="newest"
                                                aria-label="newest"
                                                checked={
                                                    sort.toLowerCase() ==
                                                    'newest'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t(
                                                'user_management.individual_user.newest'
                                            )}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleSortChange('oldest')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="oldest"
                                                value="oldest"
                                                aria-label="oldest"
                                                checked={
                                                    sort.toLowerCase() ==
                                                    'oldest'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t(
                                                'user_management.individual_user.oldest'
                                            )}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleSortChange(
                                                    'alphabetical_order'
                                                )
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="alphabetical_order"
                                                value="alphabetical_order"
                                                aria-label="alphabetical_order"
                                                checked={
                                                    sort.toLowerCase() ==
                                                    'alphabetical_order'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t(
                                                'management.nurse.alphsbetical_order'
                                            )}
                                        </Dropdown.Item>
                                    </>
                                )}

                                <Dropdown.Header
                                    className="font-bold text-black text-sm d-flex justify-content-between align-items-center"
                                    onClick={toggleApprovalExpand}
                                >
                                    {t('management.nurse.account_approval')}
                                    {isApprovalExpanded ? (
                                        <DownIcon />
                                    ) : (
                                        <UpIcon />
                                    )}
                                </Dropdown.Header>
                                {isApprovalExpanded && (
                                    <>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleFilterChange('waiting')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="waiting_for_approval"
                                                value="waiting_for_approval"
                                                aria-label="waiting_for_approval"
                                                checked={
                                                    filter.toLowerCase() ==
                                                    'waiting'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t(
                                                'management.nurse.waiting_for_approval'
                                            )}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleFilterChange('approved')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="approved"
                                                value="approved"
                                                aria-label="approved"
                                                checked={
                                                    filter.toLowerCase() ==
                                                    'approved'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t('management.nurse.approved')}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleFilterChange('rejected')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="rejected"
                                                value="rejected"
                                                aria-label="rejected"
                                                checked={
                                                    filter.toLowerCase() ==
                                                    'rejected'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t('management.nurse.rejected')}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleFilterChange('blocked')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="blocked"
                                                value="blocked"
                                                aria-label="blocked"
                                                checked={
                                                    filter.toLowerCase() ==
                                                    'blocked'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t('management.nurse.blocked')}
                                        </Dropdown.Item>
                                    </>
                                )}

                                <div className="flex cursor-pointer align-items-center justify-content-center space-x-4 px-4 py-2">
                                    <button
                                        className="bg-white border-2 border-primary  text-primary px-4 py-1 rounded"
                                        onClick={(event) => handleClear(event)}
                                        data-click-type="btn-clear"
                                    >
                                        {t('management.device.clear')}
                                    </button>
                                    <button
                                        className="text-white bg-primary rounded px-4 py-1"
                                        onClick={(event) => handleSave(event)}
                                        data-click-type="btn-save"
                                    >
                                        {t('management.device.apply')}
                                    </button>
                                </div>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
                <NurseManagementTable
                    data={paginatedUsers}
                    header={tableHeading}
                    row={rowsPerPage}
                    totalData={nurseData}
                />
                {/* <UserTable data={paginatedUsers} header={tableHeading} /> */}
                {paginatedUsers.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={setRowsPerPage}
                        handlePageChange={setCurrentPage}
                        disablePrevious={!hasPaginatedUsers}
                        disableNext={!hasPaginatedUsers}
                    />
                )}
            </div>
            {loading && <DataLoader />}
        </>
    );
};

export default NurseManagementList;
